import { DecimalPipe } from '@angular/common';
import { Component, computed, inject, resource, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SimpleApi } from '@core/api/simple.api';
import { TextTranslationService } from '@core/text/text-translation.service';
import { ProduktPris } from '@features/produktlister/produkt-pris.model';
import { ProduktType } from '@features/produktlister/produkt-type.model';
import { ProduktlisterService } from '@features/produktlister/produktlister.service';
import dayjs from 'dayjs';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { DatePicker } from 'primeng/datepicker';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';

@Component({
  selector: 'app-produktliste-form',
  imports: [
    Button,
    Select,
    FormsModule,
    Checkbox,
    DatePicker,
    InputNumber,
    DecimalPipe,
  ],
  templateUrl: './produktliste-form.component.html',
  styleUrl: './produktliste-form.component.css',
})
export class ProduktlisteFormComponent {
  textTranslationService = inject(TextTranslationService);
  plService = inject(ProduktlisterService);
  simpleApi = inject(SimpleApi);

  aarOptions = computed(() => {
    const currentYear = dayjs().year();
    const options = [];
    for (let i = currentYear + 1; i >= 2012; i--) {
      options.push({ aar: i });
    }
    return options;
  });

  getSelectOptions = (
    getter: (value: ProduktPris) => number | string,
    mapper = (v: number | string) =>
      ({ value: v, label: v + '' }) as SelectOption
  ) =>
    computed((): SelectOption[] => {
      const priser: ProduktPris[] = this.plService.produktPriser.value() || [];
      const uniqueValues: (string | number)[] = [
        ...new Set(priser.map(getter)),
      ];
      return uniqueValues.map(mapper);
    });

  opkraevningsfrekvensOptions: Signal<SelectOption[]> = this.getSelectOptions(
    (v: ProduktPris) => v.opkraevningsfrekvens,
    v => ({
      value: v,
      label: this.textTranslationService.translate(v + ''),
    })
  );
  skattekodeOptions: Signal<SelectOption[]> = this.getSelectOptions(
    (v: ProduktPris) => v.skattekode
  );
  prisgruppeOptions: Signal<SelectOption[]> = this.getSelectOptions(
    (v: ProduktPris) => v.prisgruppe
  );

  defaultFoedselsdato = dayjs().subtract(20, 'year').toDate();

  produktTyper = resource({
    params: () => this.plService.aar(),
    loader: async ({ params, abortSignal }) => {
      const produktTyper = await this.simpleApi.getList<ProduktType>(
        '/api/admin/produktlister/produkttyper',
        {
          aar: params,
        },
        abortSignal
      );

      //Nulstil produktType, hvis den ikke er valid for det pågældende år
      if (
        produktTyper.filter(x => x.id == this.plService.produktType()?.id)
          .length == 0
      ) {
        this.plService.produktType.set(undefined);
      }

      return produktTyper;
    },
  });

  praemiefritagelseVisible = computed(() => {
    const priser: ProduktPris[] = this.plService.produktPriser.value() || [];
    return (
      priser.filter(
        x => x.praemiefritagelsesFaktor && x.praemiefritagelsesFaktor > 1
      ).length > 0
    );
  });

  produktTyperGrouped = computed(() => {
    const list = this.produktTyper.value() || [];
    const grouped: ProduktTypeGrouped[] = [];

    const rap = list.filter(x => x.type === 'rap');
    if (rap.length > 0) {
      grouped.push({ navn: 'Produkter', items: rap });
    }

    const standalone = list
      .filter(x => x.type === 'standalone')
      .map(x => ({ ...x, navn: x.navn + ' - ' + x.produktTypeNummer }));
    if (standalone.length > 0) {
      grouped.push({ navn: 'Stand alone', items: standalone });
    }

    const lts = list
      .filter(x => x.type === 'lts')
      .map(x => ({ ...x, navn: x.navn + ' - ' + x.produktTypeNummer }));
    if (lts.length > 0) {
      grouped.push({ navn: 'LTS', items: lts });
    }

    return grouped;
  });

  async onClickCalc() {
    this.plService.beregnPris();
  }

  onClickReset() {
    this.plService.initialize();
  }
}

interface ProduktTypeGrouped {
  navn: string;
  items: ProduktType[];
}

interface SelectOption {
  value: number | string;
  label: string;
}
